import { Iconify } from "@repo/design-system/components/ui";
import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import "./SidebarModal.scss";

export const SidebarModal = ({ modalTitle = "", isOpen = false,
    onClose, children, defaultPosition = "right", modalSize = "w-40",
    showToggle = false, backdropClosable = true, className = "", ...props }) => {

    const [isRightPosition, setIsRightPosition] = useState(defaultPosition === "right");
    const [isVisible, setIsVisible] = useState(false);
    const [isMounted, setIsMounted] = useState(isOpen);
    const [isClient, setIsClient] = useState(false);
    useEffect(() => setIsClient(true), []);

    const basePercentage = useMemo(() => {
        const parts = (modalSize || "").split("-");
        const val = parts.length > 1 ? parseInt(parts[1], 10) : NaN;
        return isNaN(val) ? 45 : val;
    }, [modalSize]);

    const [responsiveWidth, setResponsiveWidth] = useState(`${basePercentage}%`);

    useEffect(() => {
        const calc = () => {
            const w = window.innerWidth;
            if (w <= 768) return "100%";
            if (w <= 1024) return "65%";
            return `${basePercentage}%`;
        };
        // set initial
        setResponsiveWidth(calc());

        const onResize = () => {
            setResponsiveWidth(calc());
        };
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, [basePercentage]);

    useEffect(() => {
        if (isOpen) {
            setIsMounted(true);
            setTimeout(() => setIsVisible(true), 25);
            document.body.classList.add("modal-open");
        } else {
            setIsVisible(false);
            const t = setTimeout(() => {
                setIsMounted(false);
                document.body.classList.remove("modal-open");
            }, 450);
            return () => clearTimeout(t);
        }
    }, [isOpen]);

    useEffect(() => {
        setIsRightPosition(defaultPosition === "right");
    }, [defaultPosition]);

    useEffect(() => {
        const handler = (e) => {
            if (e.key === "Escape" && isOpen) {
                e.stopImmediatePropagation();
                onClose && onClose();
            }
        };
        if (isOpen) window.addEventListener("keydown", handler, true);
        return () => window.removeEventListener("keydown", handler, true);
    }, [isOpen, onClose]);

    const handleBackdropClick = () => {
        if (backdropClosable) onClose && onClose();
    };

    const togglePosition = () => setIsRightPosition((p) => !p);

    if (!isClient) return null;
    if (!isMounted) return null;

    const modalClasses = ["sidebar-modal", isRightPosition ? "right-position" : "left-position",
        isVisible ? "open" : "closed", className].filter(Boolean).join(" ");

    const backdropClasses = `sidebar-modal-backdrop ${isVisible ? "open" : ""}`;

    return ReactDOM.createPortal(
        <>
            <div className={backdropClasses} onClick={handleBackdropClick} />
            <div className={modalClasses} style={{ width: responsiveWidth }}
                role="dialog" aria-modal="true" aria-label={modalTitle || "Sidebar Modal"} {...props}>
                <div className="sidebar-modal-header">
                    <div className="sidebar-modal-header-left">
                        <h2 title={modalTitle}>{modalTitle}</h2>
                    </div>

                    <div className="sidebar-modal-header-right">
                        {showToggle && responsiveWidth !== "100%" && (
                            <div className="toggle-switch-wrapper" onClick={togglePosition}>
                                <span className="toggle-label">{isRightPosition ? "Move to Left" : "Move to Right"}</span>
                                <div className={`toggle-switch ${isRightPosition ? "active" : ""}`} aria-hidden="true" />
                            </div>
                        )}
                        <button className="close-button" onClick={onClose} aria-label="Close">
                            <Iconify icon="gg:close-o" targetId="sidebarclose" ttLabel="Close" />
                        </button>
                    </div>
                </div>

                <div className="sidebar-modal-content">{children}</div>
            </div>
        </>,
        document.body
    );
};

SidebarModal.propTypes = {
    modalTitle: PropTypes.string,
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    children: PropTypes.node,
    defaultPosition: PropTypes.oneOf(["left", "right"]),
    modalSize: PropTypes.string,
    showToggle: PropTypes.bool,
    backdropClosable: PropTypes.bool,
    className: PropTypes.string,
};